import { useTranslation } from 'react-i18next';
import { Plus, Sparkles } from 'lucide-react';
import { FullPageCta } from '@/shared/components/FullPageCta';

interface AdminProcessingScreenProps {
  onManageIntegrations: () => void;
}

export function AdminProcessingScreen({
  onManageIntegrations,
}: AdminProcessingScreenProps) {
  const { t } = useTranslation('onboarding');

  return (
    <div className="flex-1 flex flex-col items-center justify-center px-6 sm:px-10 pb-16">
      <div className="w-full max-w-2xl mx-auto text-center flex flex-col items-center">
        <ProcessingHero />

        <span className="mt-10 text-[11px] font-medium uppercase tracking-[0.32em] text-muted-foreground">
          {t('admin.step.processing')}
        </span>
        <h1 className="mt-5 text-balance text-3xl sm:text-5xl font-bold leading-[1.05] tracking-tight text-foreground">
          {t('admin.processing.headline')}
        </h1>

        <div className="mt-7 flex flex-col items-center gap-5">
          <div aria-hidden className="h-px w-12 bg-foreground/20" />
          <p className="max-w-md text-balance text-base sm:text-lg text-muted-foreground leading-relaxed">
            {t('admin.processing.subheading')}
          </p>
        </div>

        <FullPageCta
          variant="secondary"
          onClick={onManageIntegrations}
          className="mt-10"
        >
          <Plus className="h-4 w-4" />
          {t('admin.processing.manageIntegrations')}
        </FullPageCta>
      </div>
    </div>
  );
}

function ProcessingHero() {
  return (
    <div className="relative w-36 h-36" aria-hidden>
      <div className="absolute inset-0 rounded-full bg-button-primary/10 blur-2xl animate-[processing-pulse-soft_3s_ease-in-out_infinite]" />
      <div className="absolute inset-3 rounded-full bg-button-primary/5 blur-xl animate-[processing-pulse-soft_3s_ease-in-out_infinite] [animation-delay:600ms]" />

      <div className="absolute inset-0 animate-[processing-orbit_9s_linear_infinite]">
        <span className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-button-primary shadow-[0_0_12px_rgba(12,106,187,0.6)]" />
      </div>
      <div className="absolute inset-2 animate-[processing-orbit-reverse_12s_linear_infinite]">
        <span className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 h-2 w-2 rounded-full bg-[#22d1c5] shadow-[0_0_10px_rgba(34,209,197,0.7)]" />
      </div>
      <div className="absolute inset-4 animate-[processing-orbit_15s_linear_infinite]">
        <span className="absolute bottom-0 left-1/3 h-1.5 w-1.5 rounded-full bg-button-primary/80 shadow-[0_0_8px_rgba(12,106,187,0.5)]" />
      </div>

      <div className="absolute inset-0 flex items-center justify-center">
        <div className="relative h-16 w-16 rounded-full bg-gradient-to-br from-button-primary to-[#22d1c5] flex items-center justify-center shadow-lg shadow-button-primary/30">
          <span className="absolute inset-0 rounded-full bg-button-primary/30 animate-ping" />
          <Sparkles className="relative h-7 w-7 text-white" aria-hidden />
        </div>
      </div>
    </div>
  );
}
